import { useCallback, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import type { AppConfigDTO, ImageDTO } from "@shared/types";

export type UploadStatus = "queued" | "uploading" | "done" | "error" | "cancelled";
export type FilenameMode = "as-is" | "unique-id" | "unique-timestamp";
export type DuplicateStrategy = "replace" | "copy" | "cancel";

export interface UploadItem {
  id: string;
  file: File;
  name: string; // editable base name (no extension)
  status: UploadStatus;
  progress: number;
  error?: string;
  image?: ImageDTO;
}

const CONCURRENCY = 2;

function baseName(filename: string): string {
  return filename.replace(/\.[^.]+$/, "");
}

export function useUpload(config: AppConfigDTO | null, onUploaded: (img: ImageDTO) => void) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [filenameMode, setFilenameMode] = useState<FilenameMode>("as-is");
  const [duplicateStrategy, setDuplicateStrategy] = useState<DuplicateStrategy>("copy");
  const controllers = useRef(new Map<string, AbortController>());
  const running = useRef(false);
  const folderRef = useRef<string | null>(null);

  const patch = useCallback((id: string, next: Partial<UploadItem>) => {
    setItems((list) => list.map((it) => (it.id === id ? { ...it, ...next } : it)));
  }, []);

  const validate = useCallback(
    (file: File): string | null => {
      const max = config?.maxImageBytes ?? 25 * 1024 * 1024;
      if (file.size > max) return `File exceeds the ${(max / 1024 / 1024).toFixed(0)} MB limit.`;
      if (file.size === 0) return "File is empty.";
      const accept = config?.acceptMime ?? [];
      if (file.type && accept.length && !accept.includes(file.type)) {
        return "Unsupported image type.";
      }
      return null;
    },
    [config],
  );

  const addFiles = useCallback(
    (files: File[]) => {
      const additions: UploadItem[] = files.map((file) => {
        const err = validate(file);
        return {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          name: baseName(file.name),
          status: err ? "error" : "queued",
          progress: 0,
          error: err ?? undefined,
        };
      });
      setItems((list) => [...list, ...additions]);
    },
    [validate],
  );

  const uploadOne = useCallback(
    async (item: UploadItem) => {
      const controller = new AbortController();
      controllers.current.set(item.id, controller);
      patch(item.id, { status: "uploading", progress: 0, error: undefined });
      try {
        const res = await api.uploadImage(
          {
            file: item.file,
            name: filenameMode === "as-is" ? item.name : item.name,
            folderId: folderRef.current,
            filenameMode,
            duplicateStrategy,
          },
          {
            signal: controller.signal,
            onProgress: (f) => patch(item.id, { progress: Math.round(f * 100) }),
          },
        );
        if (res.images[0]) {
          patch(item.id, { status: "done", progress: 100, image: res.images[0] });
          onUploaded(res.images[0]);
        } else {
          patch(item.id, { status: "error", error: res.errors[0]?.error ?? "Upload failed. Retry?" });
        }
      } catch (err) {
        if (controller.signal.aborted) {
          patch(item.id, { status: "cancelled" });
        } else {
          patch(item.id, { status: "error", error: err instanceof ApiError ? err.message : "Upload failed. Retry?" });
        }
      } finally {
        controllers.current.delete(item.id);
      }
    },
    [patch, filenameMode, duplicateStrategy, onUploaded],
  );

  const pump = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    try {
      // Loop until no queued items remain.
      // Re-read state each round via functional setState trick.
      for (;;) {
        const queued = await new Promise<UploadItem[]>((resolve) => {
          setItems((list) => {
            resolve(list.filter((i) => i.status === "queued"));
            return list;
          });
        });
        if (queued.length === 0) break;
        const batch = queued.slice(0, CONCURRENCY);
        await Promise.all(batch.map(uploadOne));
      }
    } finally {
      running.current = false;
    }
  }, [uploadOne]);

  const start = useCallback(
    (folderId: string | null) => {
      folderRef.current = folderId;
      void pump();
    },
    [pump],
  );

  const retry = useCallback(
    (id: string) => {
      patch(id, { status: "queued", error: undefined, progress: 0 });
      void pump();
    },
    [patch, pump],
  );

  const cancel = useCallback((id: string) => {
    controllers.current.get(id)?.abort();
    setItems((list) => list.map((it) => (it.id === id && it.status === "queued" ? { ...it, status: "cancelled" } : it)));
  }, []);

  const remove = useCallback((id: string) => {
    controllers.current.get(id)?.abort();
    setItems((list) => list.filter((it) => it.id !== id));
  }, []);

  const clearFinished = useCallback(() => {
    setItems((list) => list.filter((it) => it.status === "queued" || it.status === "uploading"));
  }, []);

  const reset = useCallback(() => {
    controllers.current.forEach((c) => c.abort());
    controllers.current.clear();
    setItems([]);
  }, []);

  return {
    items,
    filenameMode,
    duplicateStrategy,
    setFilenameMode,
    setDuplicateStrategy,
    setName: (id: string, name: string) => patch(id, { name }),
    addFiles,
    start,
    retry,
    cancel,
    remove,
    clearFinished,
    reset,
  };
}
